import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../../utils/api";
import toast from "react-hot-toast";
import { Repeat, Lock, Save, Download, History, RefreshCw, X, Check, ArrowDown, ExternalLink, Copy } from "lucide-react";
import useFeature from "../../hooks/useFeature";
import RequireFeature from "../common/RequireFeature";
import UpsellModal from "../common/UpsellModal";
import { useTheme } from "../../context/ThemeContext";
import DataTable from "../common/DataTable";
import StatusBadge from "../common/StatusBadge";
import AssetBadge from "../common/AssetBadge";
import { TokenChainLogo } from "../common/CryptoLogo";
import AssetSelect from "../common/AssetSelect";
import EmptyState from "../common/EmptyState";
import { TableSkeleton } from "../common/Skeleton";
import { chainDisplayLabel } from "../../utils/assetLogos";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Slippage presets (ACE-4): label + recommended flag keyed by bps. The server
// returns the canonical bps values under options.slippagePresets; we map them
// onto these display rows. Falls back to the spec defaults (30/50/100).
const SLIPPAGE_ROWS = [
  { key: "conservative", label: "Conservative", pct: "0.3%" },
  { key: "standard", label: "Standard", pct: "0.5%", recommended: true },
  { key: "aggressive", label: "Aggressive", pct: "1.0%" },
];

const DEFAULT_PRESETS = { conservative: 30, standard: 50, aggressive: 100 };

// Map the backend conversion statuses onto the shared StatusBadge keys/tones.
// Mirrors the normalizeTransactionStatus pattern used on the Transactions page.
//
// SOURCE OF TRUTH: Conversion.STATUS in maxelpay-backend/src/models/Conversion.js.
// Every one of its sixteen values must appear here, in lifecycle order — the
// order is what the Status filter is built from, so it reads as a progression
// rather than an alphabetical jumble.
//
// Five were missing (exchange_submitted, converted, target_transferred,
// failed_transfer, retry_pending). A conversion in any of them fell through
// conversionBadgeProps' fallback and rendered its raw key as the badge, so the
// table showed a bare "failed_transfer" chip — which is the status the
// stuck-conversion reaper sets, making the one state a merchant most needs to
// recognise the one the UI could not name.
//
// `filterLabel` is what the dropdown shows. The badge splits label/detail to
// keep the chip narrow; the filter wants the whole thing in one string.
const CONVERSION_STATUS = {
  // ── in flight ──
  queued: { status: "processing", label: "Queued", filterLabel: "Queued" },
  quoting: { status: "processing", label: "Quoting", filterLabel: "Quoting" },
  quoted: { status: "processing", label: "Quoted", filterLabel: "Quoted" },
  executing: { status: "processing", label: "Executing", filterLabel: "Executing" },
  exchange_submitted: {
    status: "processing",
    label: "Exchange submitted",
    filterLabel: "Exchange submitted",
  },
  converted: { status: "processing", label: "Converted", filterLabel: "Converted" },
  target_transferred: {
    status: "processing",
    label: "Target transferred",
    filterLabel: "Target transferred",
  },

  // ── settled ──
  completed: { status: "completed", filterLabel: "Completed" },

  // ── needs attention, funds still held ──
  retry_pending: { status: "pending", label: "Retry pending", filterLabel: "Retry pending" },
  stuck_bridge: { status: "pending", label: "Stuck (bridge)", filterLabel: "Stuck (bridge)" },
  compliance_hold: {
    status: "pending",
    label: "Compliance hold",
    filterLabel: "Compliance hold",
  },

  // ── terminal failures, funds released back to the sweep ──
  failed_no_route: {
    status: "failed",
    label: "Failed",
    detail: "no route",
    filterLabel: "Failed (no route)",
  },
  failed_slippage: {
    status: "failed",
    label: "Failed",
    detail: "slippage",
    filterLabel: "Failed (slippage)",
  },
  failed_gas: {
    status: "failed",
    label: "Failed",
    detail: "gas",
    filterLabel: "Failed (gas)",
  },
  failed_transfer: {
    status: "failed",
    label: "Failed",
    detail: "transfer",
    filterLabel: "Failed (transfer)",
  },
  cancelled: { status: "cancelled", filterLabel: "Cancelled" },
};

// Every status the system can produce, in the order declared above. Built from
// the map rather than from the loaded rows: a merchant filtering to "Executing"
// and seeing an empty table has learned something true — that none are running
// — whereas an option that vanishes when it matches nothing cannot tell them
// that, and makes the control's contents shift under them between refreshes.
// `group` drives the subheadings in the Quick Filters panel. The map above is
// already in lifecycle order, so consecutive entries sharing a group fall under
// one heading without any sorting here.
const STATUS_GROUP = {
  queued: "In flight",
  quoting: "In flight",
  quoted: "In flight",
  executing: "In flight",
  exchange_submitted: "In flight",
  converted: "In flight",
  target_transferred: "In flight",
  completed: "Settled",
  retry_pending: "Needs attention",
  stuck_bridge: "Needs attention",
  compliance_hold: "Needs attention",
  failed_no_route: "Failed",
  failed_slippage: "Failed",
  failed_gas: "Failed",
  failed_transfer: "Failed",
  cancelled: "Failed",
};

const STATUS_FILTER_OPTIONS = Object.entries(CONVERSION_STATUS).map(
  ([value, meta]) => ({ value, label: meta.filterLabel, group: STATUS_GROUP[value] })
);

const conversionBadgeProps = (raw) => {
  const key = String(raw ?? "").toLowerCase();
  const meta = CONVERSION_STATUS[key];
  if (!meta) return { status: key };
  // filterLabel belongs to the dropdown, not the chip.
  const { filterLabel, ...badge } = meta;
  return badge;
};

const fmtUsd = (v) =>
  v == null || v === ""
    ? "—"
    : `$${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

// Swap fees are cents, often fractions of one, so they get more precision
// than fmtUsd's two places: $0.0161 rather than a misleading $0.02 / $0.00.
const fmtFee = (v) =>
  v == null || v === ""
    ? "—"
    : `$${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: Math.abs(Number(v)) < 1 ? 4 : 2,
      })}`;

const fmtAmount = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("en-US", { maximumFractionDigits: 6 });
};

const fmtDate = (v) =>
  v ? new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

const truncHash = (h) => (h ? `${h.slice(0, 10)}…${h.slice(-8)}` : "—");

// A hash or address rendered as a link to the right chain's explorer.
//
// Source and target can be different chains, so each takes its own base URL.
// Falling back to the source explorer for a destination hash would produce a
// confident link to a transaction that does not exist there.
const ExplorerLink = ({ base, value, kind = "tx", children }) => {
  if (!value) return <span className="ac-dim">—</span>;
  if (!base) return <code className="ac-hash">{children}</code>;
  return (
    <a
      className="ac-hash ac-hash-link"
      href={`${base}/${kind}/${value}`}
      target="_blank"
      rel="noopener noreferrer"
      title={value}
    >
      {children}
      <ExternalLink size={12} />
    </a>
  );
};

// What the merchant actually gave up, measured rather than quoted.
//
// The stored fee columns are estimates taken at quote time and are often null;
// the difference between what went in and what arrived is the only figure that
// is always true, so it leads. Same-asset conversions can state it exactly;
// across different assets the token amounts are not comparable, so only the USD
// view is shown.
const swapCost = (c) => {
  const from = Number(c?.source_amount);
  const to = Number(c?.target_amount);
  const sameAsset = c?.source_asset && c.source_asset === c.target_asset;
  if (!sameAsset || !isFinite(from) || !isFinite(to) || from <= 0 || to <= 0) return null;
  const diff = from - to;
  return { diff, pct: (diff / from) * 100 };
};


// `sections` lets a page render the two cards separately — the Auto-conversion
// page interleaves balances and configuration history between them. Defaults to
// both, so the existing Settings tab keeps rendering exactly as before.
const AutoConversionTab = ({ sections = ['settings', 'history'] } = {}) => {
  const showSettings = sections.includes('settings');
  const showHistory = sections.includes('history');
  const navigate = useNavigate();
  const canConvert = useFeature("auto_convert");
  const { mode } = useTheme();
  const isTest = mode === "test";

  const [conversions, setConversions] = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [convError, setConvError] = useState(false);
  const [selected, setSelected] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [whitelistInput, setWhitelistInput] = useState("");

  const [config, setConfig] = useState(null);
  const [planKey, setPlanKey] = useState("");
  const [feeBps, setFeeBps] = useState(0);
  const [options, setOptions] = useState({
    targetAssets: [],
    chains: [],
    slippagePresets: DEFAULT_PRESETS,
  });

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchant/auto-conversion`
      );
      const data = await response.json();
      if (data.success) {
        const d = data.data || {};
        setConfig(d.config || {});
        setPlanKey(d.planKey || "");
        setFeeBps(d.feeBps || 0);
        setOptions({
          targetAssets: d.options?.targetAssets || [],
          chains: d.options?.chains || [],
          slippagePresets: d.options?.slippagePresets || DEFAULT_PRESETS,
        });
      } else {
        setError(true);
        toast.error(data.message || "Failed to load auto-conversion settings");
      }
    } catch (err) {
      setError(true);
      toast.error("Failed to load auto-conversion settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Inline conversion history. Auto-conversion runs on Live (mainnet) payments
  // only, so we always show mainnet rows regardless of the test/live toggle.
  const fetchConversions = useCallback(async () => {
    try {
      setConvLoading(true);
      setConvError(false);
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchant/conversions?limit=200`
      );
      const data = await response.json();
      if (data.success) {
        const rows = (data.data?.conversions || []).filter(
          (c) => !c.is_testnet
        );
        setConversions(rows);
      } else {
        setConvError(true);
      }
    } catch (err) {
      setConvError(true);
    } finally {
      setConvLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversions();
  }, [fetchConversions]);

  const update = (patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const body = {
        enabled: !!config.enabled,
        target_asset: config.target_asset,
        target_chain: config.target_chain,
        settlement_mode: config.settlement_mode,
        slippage_bps: Number(config.slippage_bps),
        min_conversion_usd: Number(config.min_conversion_usd) || 0,
        skip_if_target_asset: !!config.skip_if_target_asset,
        skip_below_min: !!config.skip_below_min,
        whitelist: config.whitelist || [],
      };
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchant/auto-conversion`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (response.status === 403) {
        setShowUpsell(true);
        return;
      }
      const data = await response.json();
      if (data.success) {
        const d = data.data || {};
        setConfig(d.config || config);
        if (d.feeBps != null) setFeeBps(d.feeBps);
        if (d.planKey) setPlanKey(d.planKey);
        toast.success("Auto-conversion settings saved");
        setDirty(false);
      } else {
        toast.error(data.message || "Failed to save settings");
      }
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchant/conversions/export`
      );
      if (response.status === 403) {
        // Server-side feature gate (csv_pdf_export) — surface the upsell.
        setShowUpsell(true);
        return;
      }
      if (!response.ok) {
        toast.error("Failed to export conversions");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to export conversions");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="ac-tab">
        <div className="ac-card">
          <p className="ac-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="ac-tab">
        <div className="ac-card">
          <p className="ac-muted">Couldn't load auto-conversion settings.</p>
          <button className="ac-btn-secondary" onClick={fetchConfig}>
            Retry
          </button>
        </div>
        <style jsx>{styles}</style>
      </div>
    );
  }

  const isSpecificChain = config.settlement_mode === "specific_chain";
  const minThreshold = config.min_conversion_usd ?? 0;
  const presets = options.slippagePresets || DEFAULT_PRESETS;
  const whitelist = config.whitelist || [];
  const planLabel = planKey
    ? planKey.charAt(0).toUpperCase() + planKey.slice(1)
    : "your plan";

  const conversionColumns = [
    {
      header: "Date",
      accessor: "created_at",
      type: "date",
      sortable: true,
      render: (value) => fmtDate(value),
    },
    // Asset and amount in separate columns. Run together ("USDT bsc 0.5") the
    // amount read as part of the asset label and the figures did not line up
    // from row to row.
    {
      header: "From",
      accessor: "source_asset",
      sortable: true,
      render: (_, row) => (
        <AssetBadge symbol={row.source_asset} chain={row.source_chain} size={24} />
      ),
    },
    {
      header: "Sent",
      accessor: "source_amount",
      sortable: true,
      render: (value, row) =>
        fmtAmount(value) ? (
          <span className="ac-amt">
            <span className="ac-amt__num">{fmtAmount(value)}</span>
            <span className="ac-amt__sym">{row.source_asset}</span>
          </span>
        ) : (
          <span className="ac-muted">—</span>
        ),
    },
    {
      header: "To",
      accessor: "target_asset",
      sortable: true,
      render: (_, row) => (
        <AssetBadge symbol={row.target_asset} chain={row.target_chain} size={24} />
      ),
    },
    {
      header: "Received",
      accessor: "target_amount",
      sortable: true,
      render: (value, row) =>
        // Nothing has arrived until the conversion completes.
        row.status === "completed" && fmtAmount(value) ? (
          <span className="ac-amt">
            <span className="ac-amt__num">{fmtAmount(value)}</span>
            <span className="ac-amt__sym">{row.target_asset}</span>
          </span>
        ) : (
          <span className="ac-muted">—</span>
        ),
    },
    {
      header: "USD value",
      accessor: "source_amount_usd",
      sortable: true,
      render: (value) => fmtUsd(value),
    },
    {
      // What the swap actually cost: value in minus value out, measured by the
      // backend (swap_fee_usd). This used to show maxelpay_fee_usd, a fee that
      // is never charged, so every row read $0.00.
      header: "Swap fee",
      accessor: "swap_fee_usd",
      sortable: true,
      render: (value) =>
        value != null ? (
          <span>{fmtFee(value)}</span>
        ) : (
          <span className="ac-muted">—</span>
        ),
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      filterable: true,
      // The full vocabulary, straight from CONVERSION_STATUS, so the filter can
      // reach every state the backend can set.
      filterOptions: STATUS_FILTER_OPTIONS,
      render: (value) => <StatusBadge {...conversionBadgeProps(value)} dot />,
    },
  ];

  return (
    <div className="ac-tab">
      {showSettings && (
      <div className="ac-card">
        {!canConvert && (
          <div className="ac-locked-banner">
            <Lock size={16} />
            <span>
              Auto-conversion is a Premium feature. This is a read-only preview —
              upgrade to enable and save changes.
            </span>
            <button
              className="ac-btn-secondary"
              onClick={() => setShowUpsell(true)}
            >
              Upgrade
            </button>
          </div>
        )}

        {isTest && (
          <div className="ac-locked-banner">
            <Lock size={16} />
            <span>
              Auto-conversion runs on Live (mainnet) payments only. Switch to
              Live to configure it.
            </span>
          </div>
        )}

        <fieldset className="ac-fieldset" disabled={!canConvert || isTest}>
          {/* Status (ACE-1) — master switch */}
          <div className={`ac-master${config.enabled ? " on" : ""}`}>
            <span className="ac-icon-chip">
              <Repeat size={18} />
            </span>
            <div className="ac-master-text">
              <span className="ac-set-title">Auto-conversion</span>
              <p className="ac-set-desc">
                {config.enabled
                  ? "Eligible incoming payments are converted automatically."
                  : "Turn on to convert eligible payments into your target asset."}
              </p>
            </div>
            <label className="ac-toggle">
              <input
                type="checkbox"
                checked={!!config.enabled}
                onChange={(e) => update({ enabled: e.target.checked })}
              />
              <span className="ac-track">
                <span className="ac-thumb" />
              </span>
            </label>
          </div>

          {/* Group: Conversion target */}
          <div className="ac-group">
            <h3 className="ac-group-title">Conversion target</h3>

            {/* Convert to (ACE-2) */}
            <div className="ac-set-row">
              <div className="ac-set-label">
                <span className="ac-set-title">Convert to</span>
                <p className="ac-set-desc">
                  The asset incoming payments are settled into.
                </p>
              </div>
              <div className="ac-set-control">
                <AssetSelect
                  kind="token"
                  className="ac-asset-select"
                  options={options.targetAssets.map((a) => ({
                    value: a,
                    label: a,
                    symbol: a,
                  }))}
                  value={config.target_asset || ""}
                  onChange={(v) => update({ target_asset: v })}
                />
              </div>
            </div>

            {/* Settlement preference (ACE-3) */}
            <div className="ac-set-row">
              <div className="ac-set-label">
                <span className="ac-set-title">Settlement chain</span>
                <p className="ac-set-desc">
                  Where the converted asset lands.
                </p>
              </div>
              <div className="ac-set-control">
                <div className="ac-choice-grid ac-choice-2">
                  <button
                    type="button"
                    className="ac-choice"
                    aria-pressed={config.settlement_mode === "same_chain"}
                    onClick={() => update({ settlement_mode: "same_chain" })}
                  >
                    <span className="ac-choice-title">Same chain</span>
                    <span className="ac-choice-sub">Settle on the payment's chain</span>
                  </button>
                  <button
                    type="button"
                    className="ac-choice"
                    aria-pressed={isSpecificChain}
                    onClick={() => update({ settlement_mode: "specific_chain" })}
                  >
                    <span className="ac-choice-title">Specific chain</span>
                    <span className="ac-choice-sub">Always settle on one chain</span>
                  </button>
                </div>
                {isSpecificChain && (
                  <div className="ac-reveal">
                    <AssetSelect
                      kind="chain"
                      className="ac-asset-select"
                      options={options.chains.map((c) => ({
                        value: c,
                        label: chainDisplayLabel(c),
                        chain: c,
                      }))}
                      value={config.target_chain || ""}
                      onChange={(v) => update({ target_chain: v })}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Group: Exclusions */}
          <div className="ac-group">
            <h3 className="ac-group-title">Exclusions</h3>

            {/* Don't convert when (ACE-5) */}
            <div className="ac-set-row">
              <div className="ac-set-label">
                <span className="ac-set-title">Skip conversion when</span>
                <p className="ac-set-desc">
                  Matching payments settle in the asset received.
                </p>
              </div>
              <div className="ac-set-control">
                <label className="ac-check">
                  <input
                    type="checkbox"
                    checked={!!config.skip_if_target_asset}
                    onChange={(e) =>
                      update({ skip_if_target_asset: e.target.checked })
                    }
                  />
                  <span>Payment is already in my target asset</span>
                </label>
                <label className="ac-check">
                  <input
                    type="checkbox"
                    checked={!!config.skip_below_min}
                    onChange={(e) => update({ skip_below_min: e.target.checked })}
                  />
                  <span>Payment is below a minimum amount</span>
                </label>
                {config.skip_below_min && (
                  <div className="ac-reveal">
                    <div className="ac-input-prefix">
                      <span className="ac-input-affix">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={minThreshold}
                        onChange={(e) =>
                          update({ min_conversion_usd: e.target.value })
                        }
                      />
                    </div>
                    <p className="ac-set-desc">
                      Payments under this USD value are settled as received.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Whitelist (ACE-6) */}
            {/* <div className="ac-set-row">
              <div className="ac-set-label">
                <span className="ac-set-title">Whitelisted addresses</span>
                <p className="ac-set-desc">
                  Payments to these addresses are never converted.
                </p>
              </div>
              <div className="ac-set-control">
                {whitelist.length > 0 && (
                  <ul className="ac-wl-list">
                    {whitelist.map((addr, idx) => (
                      <li key={addr} className="ac-wl-row">
                        <span className="ac-wl-addr">{addr}</span>
                        <button
                          type="button"
                          className="ac-wl-remove"
                          aria-label={`Remove ${addr}`}
                          onClick={() =>
                            update({
                              whitelist: whitelist.filter((_, i) => i !== idx),
                            })
                          }
                        >
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="ac-wl-add">
                  <input
                    type="text"
                    className="ac-wl-input"
                    placeholder="0x…"
                    value={whitelistInput}
                    onChange={(e) => setWhitelistInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="ac-btn-secondary"
                    onClick={() => {
                      const addr = whitelistInput.trim();
                      if (!addr || whitelist.includes(addr)) return;
                      update({ whitelist: [...whitelist, addr] });
                      setWhitelistInput("");
                    }}
                  >
                    Add
                  </button>
                  {whitelistInput && (
                    <button
                      type="button"
                      className="ac-btn-secondary"
                      onClick={() => setWhitelistInput("")}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div> */}
          </div>

          {/* Group: Execution */}
          <div className="ac-group">
            <h3 className="ac-group-title">Execution</h3>

            {/* Slippage (ACE-4) */}
            <div className="ac-set-row">
              <div className="ac-set-label">
                <span className="ac-set-title">Slippage tolerance</span>
                <p className="ac-set-desc">
                  Maximum price movement allowed before a conversion is retried.
                </p>
              </div>
              <div className="ac-set-control">
                <div className="ac-choice-grid ac-choice-3">
                  {SLIPPAGE_ROWS.map((row) => {
                    const bps = presets[row.key];
                    return (
                      <button
                        key={row.key}
                        type="button"
                        className="ac-choice"
                        aria-pressed={Number(config.slippage_bps) === Number(bps)}
                        onClick={() => update({ slippage_bps: bps })}
                      >
                        <span className="ac-choice-title">
                          {row.label}
                          {row.recommended && (
                            <span className="ac-tag">Recommended</span>
                          )}
                        </span>
                        <span className="ac-choice-sub">{row.pct}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Conversion fee (ACE-7) — read-only, server-derived */}
            {/* <div className="ac-set-row">
              <div className="ac-set-label">
                <span className="ac-set-title">Conversion fee</span>
                <p className="ac-set-desc">
                  Applied per conversion on {planLabel}.
                </p>
              </div>
              <div className="ac-set-control">
                <div className="ac-fee">
                  <span className="ac-fee-value">{feeBps / 100}%</span>
                  <span className="ac-fee-note">per conversion</span>
                </div>
              </div>
            </div> */}
          </div>
        </fieldset>

        {/* Footer save bar */}
        <div className="ac-footer">
          <span className="ac-footer-note">
            {dirty ? (
              <>
                <span className="ac-dirty-dot" />
                Unsaved changes
              </>
            ) : (
              <>
                <Check size={14} className="ac-saved-icon" />
                All changes saved
              </>
            )}
          </span>
          <RequireFeature
            feature="auto_convert"
            fallback={
              <button
                className="ac-btn-primary"
                onClick={() => setShowUpsell(true)}
                title="Auto-conversion is a Premium feature"
              >
                <Lock size={16} />
                Save changes
              </button>
            }
          >
            <button
              className="ac-btn-primary"
              onClick={handleSave}
              disabled={!dirty || saving || isTest}
            >
              <Save size={16} />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </RequireFeature>
        </div>
      </div>
      )}

      {/* Recent conversions — inline history (read-only, ungated view) */}
      {showHistory && (
      <div className="ac-card">
        <div className="ac-card-header">
          <History size={24} />
          <div>
            <h2>Recent conversions</h2>
            <p>Filter and inspect your auto-conversion history</p>
          </div>
          <div className="ac-head-actions">
            <button
              className="ac-btn-secondary"
              onClick={fetchConversions}
              disabled={convLoading}
            >
              <RefreshCw size={16} className={convLoading ? "ac-spin" : ""} />
              Refresh
            </button>
            <RequireFeature
              feature="csv_pdf_export"
              fallback={
                <button
                  className="ac-btn-secondary"
                  onClick={() => setShowUpsell(true)}
                  title="CSV export is a Premium feature"
                >
                  <Lock size={16} />
                  Export CSV
                </button>
              }
            >
              <button
                className="ac-btn-secondary"
                onClick={handleExport}
                disabled={exporting}
              >
                <Download size={16} />
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
            </RequireFeature>
          </div>
        </div>

        {convLoading ? (
          <TableSkeleton rows={5} cols={8} />
        ) : convError ? (
          <div className="ac-conv-state">
            <p className="ac-muted">Couldn't load conversions.</p>
            <button className="ac-btn-secondary" onClick={fetchConversions}>
              Retry
            </button>
          </div>
        ) : conversions.length === 0 ? (
          <EmptyState
            icon={<Repeat size={24} />}
            title="No conversions yet"
            subtitle="They'll appear here once auto-conversion runs on an eligible payment."
          />
        ) : (
          <DataTable
            columns={conversionColumns}
            data={conversions}
            searchable={false}
            // The Quick Filters control from the Transactions page: grouped
            // sections, a live count per status, and a Reset — which a bare
            // <select> of sixteen options could give none of.
            filterVariant="quick"
            // A conversion history cannot contain anything that has not happened
            // yet, so the range is capped at today rather than letting a merchant
            // pick a future window that must come back empty.
            maxDate={new Date()}
            sortable
            paginated
            pageSize={10}
            showColumnToggle={false}
            emptyMessage="No conversions match your filters."
            onRowClick={(row) => setSelected(row)}
          />
        )}
      </div>
      )}

      {selected && (
        <div className="ac-modal-overlay" onClick={() => setSelected(null)}>
          <div
            className="ac-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ac-modal-head">
              <div>
                <h3>Conversion detail</h3>
                <code className="ac-modal-id">{selected.id}</code>
              </div>
              <button
                className="ac-modal-close"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="ac-modal-body">
              {/* The swap itself, stated plainly: what went in, what came out. */}
              {/* The swap, shown as two assets with the movement between them
                  drawn rather than described. The coin treatment matches the
                  payouts balance card so an asset looks the same wherever it
                  appears in the dashboard. */}
              <div className="ac-swap">
                <span className="ac-swap-glow ac-swap-glow-a" aria-hidden="true" />
                <span className="ac-swap-glow ac-swap-glow-b" aria-hidden="true" />

                <div className="ac-swap-leg">
                  <span className="ac-swap-coin" aria-hidden="true">
                    <TokenChainLogo
                      symbol={selected.source_asset}
                      chain={selected.source_network_slug || selected.source_chain}
                      size={40}
                    />
                  </span>
                  <span className="ac-swap-body">
                    <span className="ac-swap-label">You swapped</span>
                    <span className="ac-swap-amt">
                      <strong>{fmtAmount(selected.source_amount) || "—"}</strong>
                      <span className="ac-swap-asset">{selected.source_asset}</span>
                    </span>
                    <span className="ac-swap-chain">
                      {chainDisplayLabel(selected.source_network_slug || selected.source_chain) ||
                        selected.source_chain}
                    </span>
                  </span>
                </div>

                {/* The rail carries a pulse from the asset that left to the one
                    that arrived — the one thing a static arrow cannot say. */}
                <div className="ac-swap-rail" aria-hidden="true">
                  <span className="ac-swap-rail-line" />
                  <span className="ac-swap-rail-pulse" />
                  <span className="ac-swap-arrow">
                    <ArrowDown size={15} />
                  </span>
                </div>

                <div className="ac-swap-leg ac-swap-leg-in">
                  <span className="ac-swap-coin" aria-hidden="true">
                    <TokenChainLogo
                      symbol={selected.target_asset}
                      chain={selected.target_network_slug || selected.target_chain}
                      size={40}
                    />
                  </span>
                  <span className="ac-swap-body">
                    <span className="ac-swap-label">You received</span>
                    <span className="ac-swap-amt">
                      <strong>{fmtAmount(selected.target_amount) || "—"}</strong>
                      <span className="ac-swap-asset">{selected.target_asset}</span>
                    </span>
                    <span className="ac-swap-chain">
                      {chainDisplayLabel(selected.target_network_slug || selected.target_chain) ||
                        selected.target_chain}
                    </span>
                  </span>
                </div>
              </div>

              <div className="ac-dl-row">
                <span className="ac-muted">Status</span>
                <StatusBadge {...conversionBadgeProps(selected.status)} dot />
              </div>
              <div className="ac-dl-row">
                <span className="ac-muted">USD value</span>
                <span>{fmtUsd(selected.source_amount_usd)}</span>
              </div>

              {/* Cost of the swap. MaxelPay charges nothing here — the only
                  deductions are the network's and the aggregator's, so the
                  section says so rather than leaving the merchant to wonder
                  what happened to the difference. */}
              <div className="ac-sect">
                <div className="ac-sect-head">Swap cost</div>

                {/* Same figure as the table's Swap fee column: value in minus
                    value out, which covers the aggregator, the bridge and
                    slippage together. */}
                <div className="ac-dl-row">
                  <span className="ac-muted">Swap fee</span>
                  <span className="ac-cost">
                    {selected.swap_fee_usd != null ? (
                      fmtFee(selected.swap_fee_usd)
                    ) : (
                      <span className="ac-dim">—</span>
                    )}
                  </span>
                </div>

                {(() => {
                  // Same-asset conversions can also state the cost in the
                  // asset itself, which is what the merchant sees in balances.
                  const cost = swapCost(selected);
                  return cost ? (
                    <div className="ac-dl-row">
                      <span className="ac-muted">Total deducted</span>
                      <span>
                        {fmtAmount(cost.diff)} {selected.source_asset}
                      </span>
                    </div>
                  ) : null;
                })()}

                <div className="ac-dl-row">
                  <span className="ac-muted">Aggregator &amp; bridge fees</span>
                  <span>
                    {selected.aggregator_fee_usd != null ? (
                      fmtFee(selected.aggregator_fee_usd)
                    ) : (
                      <span className="ac-dim">included in swap fee</span>
                    )}
                  </span>
                </div>
                <div className="ac-dl-row">
                  <span className="ac-muted">Network gas</span>
                  <span>
                    {selected.network_gas_usd != null ? (
                      fmtFee(selected.network_gas_usd)
                    ) : (
                      // MaxelPay's gas tank funds the swap transactions; the
                      // merchant is never charged for them.
                      <span className="ac-free">Paid by MaxelPay</span>
                    )}
                  </span>
                </div>
                <div className="ac-dl-row">
                  <span className="ac-muted">MaxelPay fee</span>
                  <span className="ac-free">Free</span>
                </div>
              </div>

              <div className="ac-sect">
                <div className="ac-sect-head">Route</div>
                <div className="ac-dl-row">
                  <span className="ac-muted">Router</span>
                  <span className="ac-router">{selected.route_provider || "—"}</span>
                </div>
                <div className="ac-dl-row">
                  <span className="ac-muted">Max slippage</span>
                  <span>
                    {selected.slippage_bps != null ? `${selected.slippage_bps / 100}%` : "—"}
                  </span>
                </div>
              </div>

              <div className="ac-sect">
                <div className="ac-sect-head">Transactions</div>
                <div className="ac-dl-row">
                  <span className="ac-muted">
                    Source ·{" "}
                    {chainDisplayLabel(selected.source_network_slug || selected.source_chain) ||
                      selected.source_chain}
                  </span>
                  <ExplorerLink base={selected.source_explorer_url} value={selected.tx_hash_source}>
                    {truncHash(selected.tx_hash_source)}
                  </ExplorerLink>
                </div>
                <div className="ac-dl-row">
                  <span className="ac-muted">
                    Destination ·{" "}
                    {chainDisplayLabel(selected.target_network_slug || selected.target_chain) ||
                      selected.target_chain}
                  </span>
                  <ExplorerLink base={selected.target_explorer_url} value={selected.tx_hash_dest}>
                    {truncHash(selected.tx_hash_dest)}
                  </ExplorerLink>
                </div>
                {selected.master_wallet_address && (
                  <div className="ac-dl-row">
                    <span className="ac-muted">Settled to</span>
                    <ExplorerLink
                      base={selected.target_explorer_url}
                      value={selected.master_wallet_address}
                      kind="address"
                    >
                      {truncHash(selected.master_wallet_address)}
                    </ExplorerLink>
                  </div>
                )}
              </div>

              {selected.failure_reason && (
                <div className="ac-dl-row">
                  <span className="ac-muted">Failure reason</span>
                  <span className="ac-fail">{selected.failure_reason}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showUpsell && (
        <UpsellModal
          feature="auto_convert"
          onClose={() => setShowUpsell(false)}
          onUpgrade={() => navigate("/dashboard/settings?tab=billing")}
        />
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

const styles = `
  .ac-tab {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .ac-card {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    padding: 24px 26px;
  }
  .ac-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
    color: var(--accent);
    flex-wrap: wrap;
  }
  .ac-card-header h2 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--ink);
    margin: 0;
  }
  .ac-card-header p {
    font-size: 13px;
    color: var(--ink-secondary);
    margin: 2px 0 0;
  }
  .ac-head-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
    flex-wrap: wrap;
  }
  .ac-muted {
    font-size: 13px;
    color: var(--ink-muted);
    margin: 4px 0 0;
  }
  .ac-locked-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    margin-bottom: 20px;
    border: 1px solid var(--line);
    border-radius: var(--r-sm);
    background: var(--accent-soft);
    color: var(--ink-secondary);
    font-size: 13px;
    flex-wrap: wrap;
  }
  .ac-locked-banner span {
    flex: 1;
    min-width: 180px;
  }
  .ac-fieldset {
    border: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .ac-fieldset:disabled {
    opacity: 0.6;
  }

  /* Master switch */
  .ac-master {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border: 1px solid var(--line);
    border-radius: var(--r-md);
    background: var(--bg-subtle);
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .ac-master.on {
    background: var(--ok-soft);
    border-color: var(--ok);
  }
  .ac-icon-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: var(--r-sm);
    background: var(--bg-surface);
    border: 1px solid var(--line);
    color: var(--accent);
  }
  .ac-master.on .ac-icon-chip {
    color: var(--ok-ink);
    border-color: var(--ok);
  }
  .ac-master-text {
    flex: 1;
    min-width: 0;
  }

  /* Groups */
  .ac-group {
    margin-top: 26px;
    padding-top: 22px;
    border-top: 1px solid var(--line);
  }
  .ac-group-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--ink-muted);
    margin: 0 0 4px;
  }

  /* Settings row: label left, control right */
  .ac-set-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 380px);
    gap: 24px;
    align-items: start;
    padding: 18px 0;
  }
  .ac-set-row + .ac-set-row {
    border-top: 1px solid var(--line);
  }
  .ac-set-label {
    min-width: 0;
  }
  .ac-set-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
  }
  .ac-set-desc {
    font-size: 13px;
    color: var(--ink-muted);
    margin: 3px 0 0;
    line-height: 1.45;
  }
  .ac-set-control {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }
  .ac-asset-select {
    max-width: 100%;
  }

  /* Reveal (nested control that appears on selection) */
  .ac-reveal {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  /* Selectable choice cards */
  .ac-choice-grid {
    display: grid;
    gap: 8px;
  }
  .ac-choice-2 { grid-template-columns: repeat(2, 1fr); }
  .ac-choice-3 { grid-template-columns: repeat(3, 1fr); }
  .ac-choice {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    padding: 11px 13px;
    text-align: left;
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    background: var(--bg-surface);
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
    font-family: var(--iv-sans);
  }
  .ac-choice:hover {
    border-color: var(--accent);
  }
  .ac-choice[aria-pressed="true"] {
    border-color: var(--accent);
    background: var(--accent-soft);
    box-shadow: inset 0 0 0 1px var(--accent);
  }
  .ac-choice-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
  }
  .ac-choice-sub {
    font-size: 12px;
    color: var(--ink-muted);
  }
  .ac-tag {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--accent-ink);
    background: var(--accent-soft);
    border-radius: var(--r-pill);
    padding: 2px 7px;
  }

  /* Checkboxes */
  .ac-check {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: var(--ink);
    cursor: pointer;
    min-height: 28px;
  }
  .ac-check input {
    width: 16px;
    height: 16px;
    accent-color: var(--accent);
    cursor: pointer;
  }

  /* Prefixed number input */
  .ac-input-prefix {
    display: inline-flex;
    align-items: stretch;
    max-width: 160px;
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    overflow: hidden;
    background: var(--bg-surface);
  }
  .ac-input-prefix:focus-within {
    border-color: var(--accent);
  }
  .ac-input-affix {
    display: inline-flex;
    align-items: center;
    padding: 0 11px;
    background: var(--bg-inset);
    color: var(--ink-muted);
    font-size: 14px;
    border-right: 1px solid var(--line-strong);
  }
  .ac-input-prefix input {
    border: none;
    outline: none;
    background: none;
    padding: 9px 12px;
    width: 100%;
    font-size: 14px;
    color: var(--ink);
    font-family: inherit;
  }

  /* Whitelist */
  .ac-wl-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .ac-wl-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    background: var(--bg-surface);
  }
  .ac-wl-addr {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--ink);
    word-break: break-all;
  }
  .ac-wl-remove {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    min-height: 32px;
    padding: 4px;
    border: none;
    background: none;
    color: var(--ink-secondary);
    cursor: pointer;
    border-radius: var(--r-sm);
  }
  .ac-wl-remove:hover {
    color: var(--danger, #c0392b);
  }
  .ac-wl-add {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .ac-wl-input {
    flex: 1;
    min-width: 160px;
    font-size: 14px;
    color: var(--ink);
    background: var(--bg-surface);
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    padding: 9px 12px;
    font-family: inherit;
  }
  .ac-wl-input:focus {
    outline: none;
    border-color: var(--accent);
  }

  /* Fee callout */
  .ac-fee {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    padding: 10px 14px;
    border-radius: var(--r-sm);
    background: var(--bg-inset);
    align-self: flex-start;
  }
  .ac-fee-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .ac-fee-note {
    font-size: 12px;
    color: var(--ink-muted);
  }

  /* Footer save bar */
  .ac-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--line);
  }
  .ac-footer-note {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--ink-muted);
  }
  .ac-dirty-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warn);
  }
  .ac-saved-icon {
    color: var(--ok-ink);
  }
  .ac-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 16px;
    border-radius: var(--r-sm);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    background: var(--primary);
    color: #fff;
    white-space: nowrap;
    font-family: var(--iv-sans);
  }
  .ac-btn-primary:hover:not(:disabled) {
    background: var(--primary-hover);
  }
  .ac-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .ac-btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 16px;
    border-radius: var(--r-sm);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--line-strong);
    background: var(--bg-surface);
    color: var(--ink);
    white-space: nowrap;
    font-family: var(--iv-sans);
  }
  .ac-btn-secondary:hover:not(:disabled) {
    border-color: var(--accent);
  }
  .ac-btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .ac-toggle {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    width: 44px;
    height: 26px;
    cursor: pointer;
  }
  .ac-toggle input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: inherit;
  }
  .ac-track {
    position: relative;
    width: 44px;
    height: 26px;
    border-radius: var(--r-pill);
    background: var(--line-strong);
    transition: background 0.2s ease;
  }
  .ac-thumb {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: var(--sh-sm);
    transition: transform 0.2s ease;
  }
  .ac-toggle input:checked + .ac-track {
    background: var(--ok);
  }
  .ac-toggle input:checked + .ac-track .ac-thumb {
    transform: translateX(18px);
  }
  .ac-asset-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ac-amount {
    font-variant-numeric: tabular-nums;
    color: var(--ink);
  }
  /* Sent / Received cells: the figure, then its symbol in a quieter tone. */
  .ac-amt {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    white-space: nowrap;
  }
  .ac-amt__num {
    font-weight: 600;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .ac-amt__sym {
    font-size: 12px;
    font-weight: 500;
    color: var(--t-500, #6c7189);
  }
  .ac-spin {
    animation: ac-spin 0.8s linear infinite;
  }
  @keyframes ac-spin {
    to { transform: rotate(360deg); }
  }
  .ac-conv-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
  }
  /* ── Conversion detail ─────────────────────────────────────────────── */

  /* The swap itself, given the weight it deserves: a merchant opening this
     wants "what did I give and what did I get" before anything else. */
  /* Source and target side by side, with the movement drawn between them.
     Stacked, the whole panel lived in the left quarter of a wide modal and the
     rest was dead space; a swap is also read left-to-right, so the layout now
     matches the sentence it is telling. */
  .ac-swap {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-5) var(--space-4);
    margin-bottom: var(--space-4);
    background: var(--bg-inset);
    border: 1px solid var(--line);
    border-radius: var(--r-md, 12px);
  }

  /* A soft wash in each asset's direction, so the panel reads as one movement
     rather than two unrelated columns. Decorative only. */
  .ac-swap-glow {
    position: absolute;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    filter: blur(56px);
    pointer-events: none;
    opacity: 0.45;
  }
  .ac-swap-glow-a { top: -110px; left: -60px; background: rgba(109, 93, 246, 0.35); }
  .ac-swap-glow-b { bottom: -110px; right: -60px; background: rgba(15, 185, 129, 0.3); }

  .ac-swap-leg {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-width: 0;
  }
  /* The logo sits on a white disc with a lifted edge — the same coin treatment
     the payouts balance card uses, so an asset looks the same everywhere. */
  .ac-swap-coin {
    flex: none;
    display: grid;
    place-items: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: var(--surface);
    border: 1px solid var(--line);
    box-shadow: 0 2px 4px rgba(16, 18, 40, 0.06), 0 8px 18px -10px rgba(16, 18, 40, 0.3);
  }
  .ac-swap-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .ac-swap-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-muted);
  }
  .ac-swap-amt {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
  }
  .ac-swap-amt strong {
    font-size: 21px;
    font-weight: 700;
    color: var(--ink);
    line-height: 1.2;
    /* Amounts are read against each other; tabular figures keep them aligned. */
    font-variant-numeric: tabular-nums;
    word-break: break-all;
  }
  .ac-swap-asset {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-secondary);
  }
  .ac-swap-chain {
    font-size: 12px;
    color: var(--ink-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ac-swap-leg-in .ac-swap-amt strong { color: var(--ok-ink, #0a8f64); }

  /* ── the rail between the two legs ── */
  .ac-swap-rail {
    position: relative;
    z-index: 1;
    flex: none;
    width: 86px;
    height: 26px;
    display: grid;
    place-items: center;
  }
  .ac-swap-rail-line {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 2px;
    transform: translateY(-1px);
    background: repeating-linear-gradient(
      to right,
      var(--line-strong) 0 4px,
      transparent 4px 8px
    );
  }
  /* The pulse is the one thing a static arrow cannot say: which way the value
     travelled. */
  .ac-swap-rail-pulse {
    position: absolute;
    top: 50%;
    width: 6px;
    height: 6px;
    margin-top: -3px;
    border-radius: 50%;
    background: var(--accent, #6d5df6);
    box-shadow: 0 0 0 3px rgba(109, 93, 246, 0.18);
    animation: ac-flow 2.4s cubic-bezier(0.45, 0, 0.55, 1) infinite;
  }
  @keyframes ac-flow {
    0%   { left: -3px; opacity: 0; }
    15%  { opacity: 1; }
    85%  { opacity: 1; }
    100% { left: calc(100% - 3px); opacity: 0; }
  }
  .ac-swap-arrow {
    position: relative;
    display: inline-grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--surface);
    border: 1px solid var(--line);
    color: var(--accent, #6d5df6);
    /* Points along the flow, which is horizontal here. */
    transform: rotate(-90deg);
  }

  /* Narrow modal: back to a stack, with the whole rail turned to match. The
     pulse keeps animating along its own axis because it is the rail that
     rotates, not the animation. */
  @media (max-width: 620px) {
    .ac-swap {
      grid-template-columns: minmax(0, 1fr);
      gap: var(--space-2);
      justify-items: start;
    }
    .ac-swap-rail { transform: rotate(90deg); margin-left: 12px; }
    .ac-swap-arrow { transform: rotate(-90deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .ac-swap-rail-pulse { animation: none; left: calc(50% - 3px); }
  }

  .ac-sect {
    margin-top: var(--space-4);
    padding-top: var(--space-3);
    border-top: 1px solid var(--line);
  }
  .ac-sect-head {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-muted);
    margin-bottom: var(--space-2);
  }
  .ac-cost {
    font-variant-numeric: tabular-nums;
  }
  .ac-dim {
    color: var(--ink-muted);
    font-weight: 400;
  }
  /* Stated positively and in the success tone: there is no platform fee on a
     swap, and the merchant should not have to infer that from a blank. */
  .ac-free {
    color: var(--ok-ink);
    font-weight: 600;
  }
  .ac-router {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: var(--accent-ink);
    background: var(--accent-soft);
    padding: 2px 8px;
    border-radius: 999px;
  }
  .ac-hash-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--accent);
    text-decoration: none;
    padding: 2px 0;
  }
  .ac-hash-link:hover {
    color: var(--accent-hover);
    text-decoration: underline;
  }
  .ac-hash-link svg {
    flex: none;
    opacity: 0.7;
  }

  .ac-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 1000;
  }
  .ac-modal {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-lg, 0 10px 40px rgba(0, 0, 0, 0.2));
    width: 100%;
    max-width: 50%;
    max-height: 85vh;
    overflow-y: auto;
  }
  .ac-modal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid var(--line);
  }
  .ac-modal-head h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--ink);
  }
  .ac-modal-id {
    font-size: 12px;
    color: var(--ink-muted);
  }
  .ac-modal-close {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ink-secondary);
    padding: 4px;
    display: inline-flex;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
  }
  .ac-modal-body {
    padding: 8px 20px 18px;
  }
  .ac-dl-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 9px 0;
    border-bottom: 1px solid var(--line);
    font-size: 14px;
    color: var(--ink);
  }
  .ac-dl-row:last-child {
    border-bottom: none;
  }
  .ac-hash {
    font-size: 12px;
    color: var(--ink-secondary);
  }
  .ac-fail {
    color: var(--danger, #c0392b);
    text-align: right;
  }
  @media (max-width: 991px){
      .ac-modal{
          max-width:100%;
      }
  }
  @media (max-width: 768px) {
    .ac-set-row {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }
  @media (max-width: 576px) {
    .ac-card {
      padding: 16px;
    }
    .ac-head-actions {
      margin-left: 0;
      width: 100%;
    }
    .ac-head-actions .ac-btn-secondary {
      flex: 1;
      justify-content: center;
      min-height: 44px;
    }
    .ac-asset-select {
      max-width: 100%;
    }
    .ac-choice-2,
    .ac-choice-3 {
      grid-template-columns: 1fr;
    }
    .ac-footer {
      flex-direction: column;
      align-items: stretch;
    }
    .ac-footer .ac-btn-primary {
      justify-content: center;
      min-height: 44px;
    }
    .ac-wl-remove {
      min-width: 44px;
      min-height: 44px;
    }
    .ac-modal-overlay {
      align-items: flex-end;
      padding: 0;
    }
    .ac-modal {
      max-width: 100%;
      max-height: 90vh;
      border-radius: var(--r-lg) var(--r-lg) 0 0;
    }
  }
`;

export default AutoConversionTab;
